import { useCallback, useState, useRef } from "react";
import axios, { CancelTokenSource } from "axios";

import { trackEvent } from "utils/analytics";
import { useInitiateImageUploadMutation } from "./InitiateImageUpload.generated";
import { SurfaceType, ActionType } from "utils/analytics/analyticsConstants";
import NoPII from "utils/NoPII";

export default function useImageUploader() {
  const [initiateImageUpload] = useInitiateImageUploadMutation();
  const [isImageUploading, setImageUploading] = useState<boolean>(false);
  const [imageUploadProgress, setImageUploadProgress] = useState<number>(0);
  const [imageUploadId, setImageUploadId] = useState<string | null>(null);
  const imageCancelTokenSourceRef = useRef<CancelTokenSource | null>(null);
  const [imageUploadError, setImageUploadError] = useState<Error | null>(null);

  const cancelImageUpload = (reason: string) => {
    if (imageCancelTokenSourceRef.current) {
      imageCancelTokenSourceRef.current.cancel(reason);
    }
    setImageUploading(false);
    setImageUploadProgress(0);
    setImageUploadId(null);
    setImageUploadError(null);
  };

  const uploadImage = useCallback(
    async (image: Blob): Promise<string | null> => {
      let loggingUploadId: string | null = null;

      try {
        trackEvent({
          eventName: "started_uploading",
          eventType: ActionType.UPLOAD_IMAGE,
          analyticsSurface: SurfaceType.IMAGE_UPLOADING,
          extraProperties: {
            extraMetadata: {
              size: image.size,
              type: NoPII(image.type),
            },
          },
        });

        imageCancelTokenSourceRef.current = axios.CancelToken.source();
        setImageUploading(true);
        setImageUploadProgress(0);
        setImageUploadId(null);
        setImageUploadError(null);

        const uploadMetadata = await initiateImageUpload();
        const preUpload = uploadMetadata.data?.initiateImageUpload;
        if (!preUpload) {
          throw new Error("Invalid upload information");
        }

        loggingUploadId = preUpload.uploadId;

        await axios.request({
          method: "PUT",
          url: preUpload.uploadUrl,
          data: image,
          cancelToken: imageCancelTokenSourceRef.current.token,
          onUploadProgress: ({ total, loaded }) => {
            const percentCompleted = total
              ? Math.round((loaded * 100) / total)
              : 0;
            setImageUploadProgress(percentCompleted);
          },
        });

        trackEvent({
          eventName: "completed_uploading",
          eventType: ActionType.UPLOAD_IMAGE,
          analyticsSurface: SurfaceType.IMAGE_UPLOADING,
          extraProperties: {
            extraMetadata: {
              uploadId: NoPII(preUpload.uploadId),
            },
          },
        });

        setImageUploading(false);
        setImageUploadProgress(100);
        setImageUploadId(preUpload.uploadId);
        imageCancelTokenSourceRef.current = null;

        return preUpload.uploadId;
      } catch (error) {
        setImageUploading(false);
        setImageUploadProgress(0);
        setImageUploadId(null);
        imageCancelTokenSourceRef.current = null;

        if (axios.isCancel(error)) {
          trackEvent({
            eventName: "cancelled_uploading",
            eventType: ActionType.UPLOAD_IMAGE,
            analyticsSurface: SurfaceType.IMAGE_UPLOADING,
            extraProperties: {
              extraMetadata: {
                uploadId: NoPII(loggingUploadId),
                reason: NoPII(error.message, {
                  allowWhitespace: true,
                }),
              },
            },
          });

          return null;
        } else {
          trackEvent({
            eventName: "failed_uploading",
            eventType: ActionType.UPLOAD_IMAGE,
            analyticsSurface: SurfaceType.IMAGE_UPLOADING,
            extraProperties: {
              extraMetadata: {
                uploadId: NoPII(loggingUploadId),
                errorName: NoPII(error.name),
                errorMessage: NoPII(error.message, {
                  allowWhitespace: true,
                }),
              },
            },
          });
          setImageUploadError(error);
          return null;
        }
      }
    },
    [initiateImageUpload, setImageUploadProgress, setImageUploadId],
  );

  return {
    isImageUploading,
    imageUploadProgress,
    imageUploadId,
    uploadImage,
    cancelImageUpload,
    imageUploadError,
  };
}
